/**
 * View 4, Succession coverage: for the roles People Leaders hold, how many
 * successors the review named at each readiness, against the role's positions.
 * Successors are only named for a People Leader's own role, so those are the
 * roles in scope here; each person counts once per role, at their best readiness.
 */
import React, { useMemo } from 'react'
import { Panel, Tile, TileRow } from './TalentDashboardParts'
import { CoverageCell, RoleTable, type RoleColumn } from './RoleTable'
import { NOT_RECORDED, count, fmtPct } from './talentDashboardData'
import type { RoleCoverage } from './talentRoleData'

export const COVERAGE_COLUMNS: RoleColumn[] = [
  { key: 'role', label: 'Role', render: (c) => c.role.role_title, sortValue: (c) => c.role.role_title },
  { key: 'function', label: 'Function', render: (c) => c.role.function || '', sortValue: (c) => c.role.function || '' },
  { key: 'positions', label: 'Positions', render: (c) => c.role.positions, sortValue: (c) => c.role.positions, className: 'tabular-nums' },
  { key: 'filled', label: 'Headcount', render: (c) => c.role.filled, sortValue: (c) => c.role.filled, className: 'tabular-nums' },
  { key: 'vacant', label: 'Vacancies', render: (c) => c.role.vacant, sortValue: (c) => c.role.vacant, className: 'tabular-nums' },
  {
    key: 'now',
    label: 'Ready now',
    render: (c) => <CoverageCell count={c.readyNow} positions={c.role.positions} />,
    sortValue: (c) => c.readyNow / Math.max(c.role.positions, 1),
  },
  {
    key: 'planned',
    label: '1 to 3 years',
    render: (c) => <CoverageCell count={c.planned} positions={c.role.positions} />,
    sortValue: (c) => c.planned / Math.max(c.role.positions, 1),
  },
  {
    key: 'potential',
    label: '3+ years',
    render: (c) => <CoverageCell count={c.potential} positions={c.role.positions} />,
    sortValue: (c) => c.potential / Math.max(c.role.positions, 1),
  },
  {
    key: 'any',
    label: 'Any readiness',
    render: (c) => <CoverageCell count={c.any} positions={c.role.positions} />,
    sortValue: (c) => c.any / Math.max(c.role.positions, 1),
  },
]

interface Sums {
  label: string
  positions: number
  readyNow: number
  planned: number
  potential: number
}

function byFunction(rows: RoleCoverage[]): Sums[] {
  const groups = new Map<string, Sums>()
  for (const c of rows) {
    const label = c.role.function || NOT_RECORDED
    const g = groups.get(label) || { label, positions: 0, readyNow: 0, planned: 0, potential: 0 }
    g.positions += c.role.positions
    g.readyNow += c.readyNow
    g.planned += c.planned
    g.potential += c.potential
    groups.set(label, g)
  }
  return [...groups.values()].sort((a, b) => b.positions - a.positions)
}

export const SuccessionView: React.FC<{
  rows: RoleCoverage[]
  positionsNote: string
  onExport?: (rows: RoleCoverage[], what: string) => void
}> = ({ rows, positionsNote, onExport }) => {
  const positions = rows.reduce((n, c) => n + c.role.positions, 0)
  const withAny = rows.filter((c) => c.any > 0).length
  const withNow = rows.filter((c) => c.readyNow > 0).length
  const successors = rows.reduce((n, c) => n + c.any, 0)
  const groups = useMemo(() => byFunction(rows), [rows])

  return (
    <div className="space-y-4">
      <TileRow min="170px">
        <Tile label="Roles in scope" value={rows.length} sub="Roles People Leaders hold" />
        <Tile label="Positions" value={positions} sub="For the roles in scope" />
        <Tile label="Roles with a successor" value={fmtPct(withAny, rows.length)} sub={`${count(withAny, 'role')}, any readiness`} />
        <Tile label="Roles with a Ready Now successor" value={fmtPct(withNow, rows.length)} sub={count(withNow, 'role')} />
        <Tile
          label="Successors per position"
          value={positions ? (successors / positions).toFixed(1) : 'n/a'}
          sub={`${successors} named for ${positions} positions`}
        />
      </TileRow>
      <p className="text-[11px] text-gray-500">{positionsNote}</p>

      <RoleTable
        title="Coverage by role"
        note="Successors at each readiness, with their share of the role's positions. 100% means one successor for every position."
        rows={[...rows].sort((a, b) => b.role.positions - a.role.positions)}
        columns={COVERAGE_COLUMNS}
        onExport={onExport && (() => onExport(rows, 'Succession coverage by role'))}
      />

      <Panel title="By function" note="Share of each function's positions with a successor at each readiness.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wide text-gray-500">
                {['Function', 'Positions', 'Ready now', '1 to 3 years', '3+ years'].map((h) => (
                  <th key={h} scope="col" className="px-2 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.label} className="border-b border-gray-100">
                  <td className="px-2 py-1.5 text-gray-700">{g.label}</td>
                  <td className="px-2 py-1.5 tabular-nums">{g.positions}</td>
                  <td className="px-2 py-1.5"><CoverageCell count={g.readyNow} positions={g.positions} /></td>
                  <td className="px-2 py-1.5"><CoverageCell count={g.planned} positions={g.positions} /></td>
                  <td className="px-2 py-1.5"><CoverageCell count={g.potential} positions={g.positions} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}
